import classnames from 'classnames/bind';

import { relativeTimeFromNow } from 'services/date';
import { useAccount } from 'hooks/api/useAccount';
import { useApplicationUsageStatistics } from 'application/hooks/useApplicationUsageStatistics';
import { useCurrentApplication } from 'application/hooks/useCurrentApplication';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useHasAccess } from 'hooks/useHasAccess';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { getDefaultDashboard } from 'services/defaultDashboards';

import { DashboardView } from 'dashboards/views/DashboardView/DashboardView';

import * as PageLayout from 'components/PageLayout';
import { Heading } from 'components/Heading/Heading';
import { Loader } from 'components/Loader/Loader';
import { Pill } from 'components/Pill/Pill';
import { GatewaysUtilizationCard } from 'organizations/views/OrganizationUtilization/cards/GatewaysUtilizationCard';
import {
  WorkspaceCloudStorageUtilizationCard,
  WorkspaceDeploymentsUtilizationCard,
  WorkspaceRecordedAnalyticsUtilizationCard,
  WorkspaceRetentionUtilizationCard,
  WorkspaceStreamsUtilizationCard,
} from 'application/components/WorkspaceUtilizationCards/WorkspaceUtilizationCards';

import { OverviewDeployments } from './Deployments';
import { OverviewEvents } from './Events';
import { OverviewFooter } from './Footer';

import styles from './Overview.module.scss';

const c = classnames.bind(styles);

export function Overview() {
  const { data: account } = useAccount();
  const { data: application } = useCurrentApplication();
  const { data: currentPlan } = useCurrentPlan();
  const { data: usageStatistics } = useApplicationUsageStatistics();
  const [hasAccess] = useHasAccess();

  const lastUpdate = relativeTimeFromNow(usageStatistics?.updated_at);

  // Get application-specific default dashboard ID
  const defaultDashboardId = getDefaultDashboard(
    account?.account_attributes?.default_dashboards,
    application?.id
  );

  // Validate dashboard exists and user has monitor access
  const {
    data: dashboard,
    error: dashboardError,
    isLoading: isDashboardLoading,
  } = useDashboard(defaultDashboardId, {
    enabled: Boolean(defaultDashboardId),
  });

  const hasMonitorAccess = hasAccess('monitor');
  const shouldShowDashboard =
    defaultDashboardId && hasMonitorAccess && dashboard && !dashboardError;

  // Show loading spinner while checking if default dashboard should be displayed
  if (defaultDashboardId && hasMonitorAccess && isDashboardLoading) {
    return (
      <PageLayout.Root className={c('root')}>
        <PageLayout.Container>
          <div className={c('wrap')}>
            <Loader text="Loading dashboard..." />
          </div>
        </PageLayout.Container>
      </PageLayout.Root>
    );
  }

  // If a default dashboard is set and valid, render it instead of showing the overview
  if (shouldShowDashboard) {
    return <DashboardView dashboardId={defaultDashboardId} readOnly />;
  }

  return (
    <PageLayout.Root className={c('root')}>
      <PageLayout.Container>
        <div className={c('wrap')}>
          <div className={c('overview')}>
            {usageStatistics && application && currentPlan !== undefined && (
              <div className={c('overview-cards')}>
                <div className={c('deployments', 'overview-card')}>
                  <OverviewDeployments />
                </div>
                <div className={c('gateways', 'overview-card')}>
                  <GatewaysUtilizationCard />
                </div>
                <div className={c('organization-name', 'overview-card')}>
                  <Heading level="2">
                    {application.name || 'Workspace'} utilization
                  </Heading>
                </div>
                <div className={c('workspace-utilization', 'overview-card')}>
                  <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 md:grid-cols-2">
                    <li className="min-w-0">
                      <WorkspaceStreamsUtilizationCard />
                    </li>
                    <li className="min-w-0">
                      <WorkspaceRecordedAnalyticsUtilizationCard />
                    </li>
                    {currentPlan?.can_retain_recordings && (
                      <li className="min-w-0">
                        <WorkspaceRetentionUtilizationCard />
                      </li>
                    )}
                    <li className="min-w-0">
                      <WorkspaceCloudStorageUtilizationCard />
                    </li>
                    <li className="min-w-0 md:col-span-2">
                      <WorkspaceDeploymentsUtilizationCard />
                    </li>
                  </ul>
                </div>
                <div className={c('time-tag')}>
                  <Pill icon="rotate">Updated {lastUpdate}</Pill>
                </div>
              </div>
            )}

            <div className={c('events')}>
              <OverviewEvents />
            </div>
            <div className={c('footer')}>
              <OverviewFooter />
            </div>
          </div>
        </div>
      </PageLayout.Container>
    </PageLayout.Root>
  );
}
